import React, { useCallback, useEffect } from "react";
import toast from "react-hot-toast";
import useApi from "../useApi";

export interface IRiskRatingRes {
  msg: string;
  data: {
    verificationResult: string;
  };
  status: string;
}

const useFetchRiskRating = (billingCycleId: string) => {
  const [riskRatingData, setRiskRatingData] = React.useState<
    IRiskRatingRes["data"] | null
  >(null);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | null>(null);

  const { makeApiCall } = useApi({
    url: `/rtm/patient-dashboard/pdf/v3/risk-rating/${billingCycleId}`,
    method: "GET",
  });

  const getRiskRatingData = useCallback(async () => {
    if (!billingCycleId) {
      console.log("No patientId provided, skipping API call");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const { response, error: apiError } = await makeApiCall<
        undefined,
        IRiskRatingRes
      >();

      if (apiError) {
        throw new Error(apiError.msg || "Failed to fetch risk rating");
      }

      if (response?.status === "success") {
        setRiskRatingData(response.data);
      } else {
        throw new Error(response?.msg || "Failed to load risk rating data");
      }
    } catch (error: any) {
      console.error("Error in getRiskRatingData:", error);
      setError(error.message);
      // toast.error(error.message || "Unable to load risk rating data. Please refresh and try again.");
      setRiskRatingData(null);
    } finally {
      setIsLoading(false);
    }
  }, [billingCycleId, makeApiCall]);

  useEffect(() => {
    if (billingCycleId) {
      getRiskRatingData();
    }
  }, [billingCycleId, getRiskRatingData]);

  return {
    riskRatingData,
    riskRatingLoading: isLoading,
    error,
    refetch: getRiskRatingData,
  };
};

export default useFetchRiskRating;
